import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingCart } from 'lucide-react';
import { type FormField } from '@/types';
import { TicketCard } from './TicketCard';

interface Ticket {
  id: string;
  name: string;
  description: string | null;
  price: number;
  capacity: number;
  formSchema: FormField[] | null;
  type: 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL';
}

interface TicketsListProps {
  tickets: Ticket[];
  cart: Array<{ ticket: Ticket; count: number }>;
  isMember: boolean;
  isRegistered?: boolean;
  onCheckout: () => void;
  onUpdateCart: (ticket: Ticket, delta: number) => void;
  onLoginRequired: () => void;
}

export function TicketsList({
  tickets,
  cart,
  isMember,
  isRegistered = false,
  onCheckout,
  onUpdateCart,
  onLoginRequired,
}: TicketsListProps) {
  const cartCount = cart.reduce((acc, item) => acc + item.count, 0);
  const cartTotal = cart.reduce((acc, item) => acc + item.ticket.price * item.count, 0);

  return (
    <Card className="min-w-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 pb-2 sm:px-6">
        <CardTitle>{isRegistered ? 'Book for guests' : 'Registration'}</CardTitle>
        {cartCount > 0 && (
          <Badge variant="default" className="ml-2 shrink-0">
            {cartCount} in Cart
          </Badge>
        )}
      </CardHeader>
      <CardDescription className="px-4 pb-2 sm:px-6">
        {isRegistered ? "You're registered. Add tickets for guests below." : 'Select tickets to register'}
      </CardDescription>
      <CardContent className="space-y-3 px-4 sm:space-y-4 sm:px-6">
        {tickets.map((ticket) => {
          const cartItem = cart.find((item) => item.ticket.id === ticket.id);
          return (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              isMember={isMember}
              cartCount={cartItem?.count || 0}
              onUpdateCart={onUpdateCart}
              onLoginRequired={onLoginRequired}
            />
          );
        })}
        {tickets.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-2">
            No tickets information available.
          </div>
        )}
      </CardContent>
      {cart.length > 0 && (
        <CardFooter className="flex min-w-0 flex-col gap-4 border-t px-4 pt-4 sm:px-6">
          <div className="flex w-full min-w-0 flex-col gap-3">
            <div className="flex min-w-0 flex-col font-bold">
              <span className="text-xs font-normal text-muted-foreground">
                Total ({cartCount} tickets)
              </span>
              <span>₹{cartTotal}</span>
            </div>
            <Button onClick={onCheckout} className="h-11 w-full max-w-full shrink-0 shadow-sm">
              <ShoppingCart className="mr-2 h-4 w-4 shrink-0" />
              Checkout
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
}
